import * as React from 'react'
import * as moment from 'moment-timezone'
import { getStepConfig, resolveLabel, formatNodeCategoryEyebrow, STEP_CATEGORIES } from '../../step-config'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { useJourneyContext } from '../../context'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import type { IRecurrenceSchedule } from '@pushly/models/lib/interfaces/recurrence-schedule'
import { getJourneyDisplayItemType } from '../../../../_utils/journeys'
import { StepType, JourneyType } from '@pushly/models/lib/enums/journeys'
import { getTriggerDisplayLabel } from '../../trigger-config'
import { stringFromNumericalDays } from '@pushly/aqe/lib/utils/string-from-numerical-days'

const DAY_NAME_TO_ISO: Record<string, number> = {
    monday: 1, tuesday: 2, wednesday: 3, thursday: 4,
    friday: 5, saturday: 6, sunday: 7,
}

function formatTime12h(time24: string): string {
    const m = moment(time24, 'HH:mm')
    return m.isValid() ? m.format('h:mm A') : time24
}

function formatScheduleSummary(schedule: IRecurrenceSchedule): string | null {
    const { type, specific_time, allowed_days, specific_day } = schedule
    const timePart = specific_time ? formatTime12h(specific_time) : null

    if (type === 'daily' || type === 'weekly') {
        const isoDays = (allowed_days ?? [])
            .map((d) => DAY_NAME_TO_ISO[d.toLowerCase()])
            .filter(Boolean) as number[]

        const dayStr = isoDays.length > 0
            ? stringFromNumericalDays(isoDays as any[])
            : null

        const prefix = !dayStr ? 'Daily' : dayStr
        return timePart ? `${prefix} at ${timePart}` : prefix
    }

    if (type === 'monthly_specific' && specific_day) {
        const ordinalDay = moment(specific_day, 'D').format('Do')
        const prefix = `${ordinalDay} of each month`
        return timePart ? `${prefix} at ${timePart}` : prefix
    }

    if (type === 'monthly_relative') {
        return timePart ? `Monthly at ${timePart}` : 'Monthly'
    }

    return null
}

export const JourneyNodeLabel = ({ node, loading }: { node: IJourneyStep; loading?: boolean }) => {
    const [state] = useJourneyContext()
    const config = getStepConfig(node)
    const itemType = getJourneyDisplayItemType(state.journey, state.domain.ecommConfig?.itemType)

    const eyebrow = (() => {
        if (!config || config.kind !== 'user') return null
        const catConfig = STEP_CATEGORIES[config.category]
        if (!catConfig) return null
        return formatNodeCategoryEyebrow(catConfig.label)
    })()

    // Entry node subtitle: trigger label or schedule summary
    const entrySubtitle = (() => {
        if (node.type !== StepType.TRIGGER || !state.journey) return null
        const journey = state.journey

        if (journey.type === JourneyType.TRIGGER) {
            return getTriggerDisplayLabel(journey.configuration.computed_trigger_type, '', itemType) || null
        }

        if (journey.type === JourneyType.RECURRING && journey.configuration.schedule) {
            return formatScheduleSummary(journey.configuration.schedule)
        }

        return null
    })()

    const subtitle = (() => {
        if (loading) return <Loading3QuartersOutlined spin={true} />
        if (entrySubtitle) return entrySubtitle
        return config?.getSubtitle?.(node) ?? null
    })()

    return (
        <div className="label">
            {state.journey && (
                <>
                    {eyebrow && <span className="eyebrow">{eyebrow}</span>}
                    <span className="header">{config ? resolveLabel(config, itemType) : undefined}</span>
                    <span className="subtitle">{subtitle}</span>
                </>
            )}
        </div>
    )
}
